import React from 'react'
import { SEO, createStructuredData } from '../components/SEO'
import { Link } from 'react-router-dom'
import { 
  CalculatorIcon, 
  ClockIcon, 
  CurrencyDollarIcon, 
  ScaleIcon,
  ArrowRightIcon,
  PhoneIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  SparklesIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline'
import { StarIcon } from '@heroicons/react/24/solid'

const calculators = [
  {
    name: 'Closing Cost Calculator',
    description: 'Calculate all standard Illinois closing costs, including county-specific transfer taxes.',
    icon: CalculatorIcon,
    to: '/closing-costs',
    highlight: 'Most Popular',
  },
  {
    name: 'Purchasing Power Calculator',
    description: 'Calculate your debt-to-income ratio and maximum home price based on Illinois guidelines.',
    icon: BanknotesIcon,
    to: '/dti-calculator',
  },
  {
    name: 'Mortgage Calculator',
    description: 'Calculate monthly payments, including P&I, taxes, insurance, and PMI.',
    icon: CurrencyDollarIcon,
    to: '/mortgage-calculator',
  },
  {
    name: 'Timeline Calculator',
    description: 'Get an interactive timeline with all critical deadlines for your Illinois home purchase.',
    icon: ClockIcon,
    to: '/timeline-calculator',
  },
]

const features = [
  {
    name: 'Illinois-Specific Calculations',
    description: 'All calculators are tailored to Illinois laws, tax rates, and local requirements.',
    icon: StarIcon,
  },
  {
    name: 'Real-Time Updates',
    description: 'Our tools stay current with the latest Illinois regulations and market conditions.',
    icon: ClockIcon,
  },
  {
    name: 'Attorney-Reviewed',
    description: 'Every calculation and timeline is verified by experienced Illinois real estate attorneys.',
    icon: ScaleIcon,
  },
]

const testimonials = [
  {
    content: "The closing cost calculator was incredibly accurate. It helped me budget properly for my Chicago home purchase.",
    author: "Sarah Mitchell",
    location: "Chicago, IL",
  },
  {
    content: "The timeline tool made it so much easier to understand the home buying process in Illinois. Highly recommend!",
    author: "Michael Rodriguez",
    location: "Naperville, IL",
  },
]

const structuredData = {
  "@type": "WebSite",
  "name": "Northside Legal Home Buyer Calculators",
  "url": "https://calculators.northsidelegal.com",
  "description": "Comprehensive suite of calculators to guide home buyers through the purchasing process in Illinois",
  "publisher": {
    "@type": "Organization",
    "name": "Northside Legal",
    "logo": {
      "@type": "ImageObject",
      "url": "https://calculators.northsidelegal.com/logo.png"
    }
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://calculators.northsidelegal.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "mainEntity": [
    {
      "@type": "Calculator",
      "name": "Closing Costs Calculator",
      "description": "Estimate all standard Illinois closing costs",
      "url": "https://calculators.northsidelegal.com/closing-costs"
    },
    {
      "@type": "Calculator",
      "name": "Purchasing Power Calculator",
      "description": "Calculate the maximum home loan you can get based on your purchasing power",
      "url": "https://calculators.northsidelegal.com/dti-calculator"
    },
    {
      "@type": "Calculator",
      "name": "Mortgage Calculator",
      "description": "Estimate monthly mortgage payments",
      "url": "https://calculators.northsidelegal.com/mortgage-calculator"
    },
    {
      "@type": "Calculator",
      "name": "Purchasing Power Calculator",
      "description": "Determine your maximum home price",
      "url": "https://calculators.northsidelegal.com/purchasing-power-calculator"
    },
    {
      "@type": "Calculator",
      "name": "Timeline Calculator",
      "description": "Plan your home buying timeline",
      "url": "https://calculators.northsidelegal.com/timeline-calculator"
    }
  ]
}

export default function Home() {
  return (
    <div>
      <SEO 
        title="Illinois Home Buyer Calculators | Free Real Estate Tools"
        description="Comprehensive suite of free calculators for Illinois home buyers. Estimate mortgage, closing costs, purchasing power, and more with expert tools."
        keywords={["home buying calculators", "Illinois real estate", "mortgage tools", "home purchase planning"]}
        canonicalUrl="https://calculators.northsidelegal.com"
        structuredData={structuredData}
      />

      {/* Add script tag for structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Hero section with gradient background */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-50 via-white to-brand-50" />
        <div className="relative pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold text-gray-900 sm:text-5xl md:text-6xl">
              Empower Your
              <span className="text-brand-600 block mt-2">Illinois Home Purchase</span>
            </h1>
            <div className="space-y-4 max-w-3xl mx-auto px-4 sm:px-6 mt-8">
              <div className="flex items-start space-x-4 bg-brand-50/50 p-4 sm:p-6 rounded-lg hover:bg-brand-100/50 transition-all duration-300 ease-in-out sm:space-x-6">
                <LightBulbIcon className="h-8 w-8 text-brand-600 flex-shrink-0 mt-1" />
                <span className="text-left">We've designed these tools to empower you to easily understand home buying costs and processes.</span>
              </div>
              <div className="flex items-start space-x-4 bg-brand-50/50 p-4 sm:p-6 rounded-lg hover:bg-brand-100/50 transition-all duration-300 ease-in-out sm:space-x-6">
                <CalculatorIcon className="h-8 w-8 text-brand-600 flex-shrink-0 mt-1" />
                <p className="mt-4 text-lg text-gray-600">
                  Use our calculators for calculating closing costs, maximum home purchasing power, mortgages, and closing timeline to plan your home buying journey.
                </p>
              </div>
              <div className="flex items-start space-x-4 bg-brand-50/50 p-4 sm:p-6 rounded-lg hover:bg-brand-100/50 transition-all duration-300 ease-in-out sm:space-x-6">
                <SparklesIcon className="h-8 w-8 text-brand-600 flex-shrink-0 mt-1" />
                <span className="text-left">Get accurate estimates and expert guidance on your Illinois home purchase, all in one place.</span>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row sm:justify-center space-y-4 sm:space-y-0 sm:space-x-4 px-4 sm:px-0">
              <a 
                href="#calculators" 
                className="btn-primary text-lg py-4 px-8 w-full sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('calculators')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                View Calculators
                <ArrowRightIcon className="ml-2 h-6 w-6" />
              </a>
              <a 
                href="tel:847-318-3770" 
                className="btn-secondary text-lg py-4 px-8 w-full sm:w-auto"
              >
                <PhoneIcon className="mr-2 h-6 w-6" />
                Free Consultation
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.name}
                className="relative bg-white overflow-hidden h-full"
              >
                <div className="h-full flex flex-col p-6 bg-gray-50/50">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <feature.icon 
                        className="h-8 w-8 text-brand-600" 
                        aria-hidden="true" 
                      />
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      {feature.name}
                    </h3>
                  </div>
                  <p className="mt-4 text-base text-gray-500 flex-grow">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calculators section */}
      <div id="calculators" className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-display font-bold text-gray-900 text-center mb-12">
            Choose Your Calculator
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {calculators.map((calculator) => (
              <Link
                key={calculator.name}
                to={calculator.to}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="group relative block transform transition-all duration-200 hover:-translate-y-1 h-full"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-brand-600 to-brand-400 rounded-lg blur opacity-25 group-hover:opacity-100 transition duration-200" />
                <div className="relative bg-white p-6 rounded-lg shadow-md group-hover:shadow-xl transition-all duration-200 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <span className="rounded-lg inline-flex p-3 bg-brand-50 text-brand-600 ring-4 ring-white">
                        <calculator.icon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {calculator.name}
                        </h3>
                        {calculator.highlight && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                            {calculator.highlight}
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-brand-500 group-hover:translate-x-1 transition-all duration-200" />
                  </div>
                  <p className="mt-auto text-gray-600 flex-grow">
                    {calculator.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-gray-900">
              What Our Users Say
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex space-x-1 text-brand-500 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5" />
                  ))}
                </div>
                <p className="text-gray-600 italic">"{testimonial.content}"</p>
                <div className="mt-4">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
